// NotificationsBell — top-bar bell + popover panel.
//
// Surfaces five categories of notifications:
//   • Trial / paid-subscription ending within 7 days (warn).
//   • Auto-closed positions — stop hit (warn) / target hit (info).
//   • Option contracts expiring within 3 trading days (warn).
//   • Recent opens — last 3 trades (info).
//   • Always-on "live market data" status footer.
//
// Each item has a stable string key. The X on a notification dismisses
// that specific key into localStorage, so a notification the user has
// already actioned won't keep reappearing on every poll. Dismissals are
// stored with a TTL (default 30 days) so the dismissed set can't grow
// unboundedly — and so a brand-new notification that happens to reuse
// an old key (vanishingly rare given the keys are id-based) doesn't get
// permanently silenced.
//
// The red counter on the bell shows the number of un-dismissed `warn`
// items; opening the panel refreshes data and a 60s interval keeps it
// fresh while open. A lighter 2-minute background poll keeps the
// counter accurate even when the panel is closed.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, DollarSign, Activity, X, ChevronRight, Clock, Target, Shield } from 'lucide-react';
import { apiGetPortfolioHeat, apiGetTrades } from '../../utils/backendApi';
import { useAuth } from '../../context/AuthContext';
import { isLiveData } from '../../utils/marketEngine';
import { formatDateOnly } from '../../utils/format';

// ── Dismissal storage ───────────────────────────────────────────────────────
// Shape: { [key]: timestampMs }. Reading + writing is one localStorage round
// trip per call; the dataset is small (typically <50 keys ever).

const DISMISS_KEY = 'stokora.notif.dismissed';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days

function loadDismissals() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    // Drop entries older than the TTL on every read — keeps the store
    // bounded without a separate sweep job.
    const cutoff = Date.now() - DISMISS_TTL_MS;
    const fresh = {};
    for (const [k, ts] of Object.entries(parsed)) {
      if (typeof ts === 'number' && ts > cutoff) fresh[k] = ts;
    }
    return fresh;
  } catch { return {}; }
}

function saveDismissals(map) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(map)); }
  catch { /* private mode / quota — ignore */ }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function daysUntil(iso) {
  if (!iso) return null;
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const expiry = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  return Math.ceil((expiry - Date.now()) / 86_400_000);
}

function fmtPnl(n) {
  return `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(2)}`;
}

// ── Item builder ────────────────────────────────────────────────────────────

function buildItems({ heat, trades, user }) {
  const items = [];

  // 1. Subscription / trial ending in ≤ 7 days. The Layout already shows a
  //    persistent banner for this, but mirroring it into the bell makes the
  //    red counter the canonical "you have to act" signal.
  if (user && user.role !== 'admin') {
    const status = user.subscription_status;
    if (status === 'trial' && user.trial_ends_at) {
      const d = daysUntil(user.trial_ends_at);
      if (d != null && d <= 7) {
        items.push({
          key:   `sub-trial-${String(user.trial_ends_at).slice(0, 10)}`,
          kind:  d <= 0 ? 'warn' : 'warn',
          icon:  Shield,
          title: d <= 0 ? 'Trial expired' : `Trial ends in ${d}d`,
          body:  d <= 0
                  ? 'Subscribe to keep generating signals and trading.'
                  : 'Subscribe before it ends to avoid losing access.',
          to:    '/dashboard',
        });
      }
    }
    if (status === 'active' && user.subscription_ends_at) {
      const d = daysUntil(user.subscription_ends_at);
      if (d != null && d <= 7) {
        items.push({
          key:   `sub-active-${String(user.subscription_ends_at).slice(0, 10)}`,
          kind:  d <= 0 ? 'warn' : 'warn',
          icon:  Shield,
          title: d <= 0 ? 'Subscription expired' : `Subscription ends in ${d}d`,
          body:  d <= 0
                  ? 'Extend your subscription to keep access.'
                  : 'Renew before it ends to avoid losing access.',
          to:    '/dashboard',
        });
      }
    }
  }

  // 2. Auto-closed positions — surfaced from trades closed in the last 24h
  //    where the background autoCloseMonitor flipped the row (auto_close_kind
  //    is 'target' or 'stop'). Stop hits are warns; target hits are info.
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  for (const t of (trades || [])) {
    if (t.status !== 'closed' || !t.auto_close_kind) continue;
    const closedAtMs = t.closed_at ? new Date(t.closed_at).getTime() : null;
    if (closedAtMs == null || closedAtMs < cutoff) continue;
    const pnl = parseFloat(t.realized_pnl ?? 0);
    const isWin = t.auto_close_kind === 'target';
    items.push({
      key:   `autoclose-${t.id}`,
      kind:  isWin ? 'info' : 'warn',
      icon:  isWin ? Target : AlertTriangle,
      title: isWin
              ? `${t.ticker} hit take-profit`
              : `${t.ticker} hit stop-loss`,
      body:  `Closed at $${parseFloat(t.close_price).toFixed(2)} · ${fmtPnl(pnl)}`,
      ts:    t.closed_at,
      to:    '/dashboard/trading',
    });
  }

  // 3. Option contracts expiring within 3 trading days.
  for (const t of (trades || []).filter(x => x.status === 'open' && x.asset_type === 'option')) {
    const d = daysUntil(t.option_expiry);
    if (d != null && d >= 0 && d <= 3) {
      items.push({
        key:   `expiry-${t.id}`,
        kind:  'warn',
        icon:  Clock,
        title: `${t.ticker} option expires in ${d}d`,
        body:  `${t.option_type?.toUpperCase()} ${t.option_strike} · exp ${formatDateOnly(t.option_expiry)}`,
        to:    '/dashboard/trading',
      });
    }
  }

  // 4. Three most recent opens (skip closes — auto-closes get their own
  //    dedicated item above, and a closed trade showing up as both an "auto
  //    closed" warn AND a "Closed BUY ..." info felt noisy).
  const recentOpens = (trades || [])
    .filter(t => t.status === 'open')
    .slice(0, 3);
  for (const t of recentOpens) {
    items.push({
      key:   `trade-open-${t.id}`,
      kind:  'info',
      icon:  DollarSign,
      title: `Opened ${t.direction} ${t.ticker}`,
      body:  `${t.quantity} @ $${parseFloat(t.price).toFixed(2)}`,
      ts:    t.created_at,
      to:    '/dashboard/trading',
    });
  }

  // 5. Heat warnings — disabled at the source in portfolioValuation, kept
  //    here as a passthrough in case it ever comes back.
  for (const w of heat?.warnings || []) {
    items.push({
      key:   `heat-${w.kind || w.message}`,
      kind:  'warn',
      icon:  AlertTriangle,
      title: 'Portfolio risk',
      body:  w.message,
      to:    '/dashboard/trading',
    });
  }

  // 6. Always-on data-source line — sentinel item, never dismissible.
  items.push({
    key:        'data-source',
    kind:       isLiveData() ? 'ok' : 'info',
    icon:       Activity,
    title:      isLiveData() ? 'Live market data' : 'Simulated data',
    body:       isLiveData()
                ? 'Quotes from Polygon · refreshed every 30s'
                : 'Set VITE_POLYGON_API_KEY for live quotes',
    to:         null,
    pinned:     true,    // skip dismissal X
  });

  return items;
}

const KIND_STYLE = {
  warn: { color: 'var(--amber)',      tint: 'var(--amber-dim)',   border: 'rgba(245,158,11,0.25)' },
  info: { color: 'var(--accent-soft)',tint: 'var(--accent-dim)',  border: 'rgba(91,106,240,0.18)' },
  ok:   { color: 'var(--green)',      tint: 'var(--green-dim)',   border: 'rgba(34,197,94,0.22)' },
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const [open, setOpen]               = useState(false);
  const [rawItems, setRawItems]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [dismissed, setDismissed]     = useState(() => loadDismissals());
  const wrapRef = useRef(null);

  // Outside-click + Esc to close panel.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [heat, trades] = await Promise.all([
        apiGetPortfolioHeat().catch(() => null),
        apiGetTrades().catch(() => []),
      ]);
      setRawItems(buildItems({ heat, trades, user }));
    } catch { /* keep prior items */ }
    setLoading(false);
  }, [user]);

  // Refresh when the panel opens, plus a 60 s tick while it's open so freshly
  // hit auto-closes show up promptly.
  useEffect(() => {
    if (!open) return;
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [open, load]);

  // Background warn-counter poll — runs whether or not the panel is open so
  // the red dot stays accurate.
  const [warnCount, setWarnCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const [heat, trades] = await Promise.all([
        apiGetPortfolioHeat().catch(() => null),
        apiGetTrades().catch(() => []),
      ]);
      if (cancelled) return;
      const built = buildItems({ heat, trades, user });
      const visible = built.filter(i => !dismissed[i.key]);
      setWarnCount(visible.filter(i => i.kind === 'warn').length);
      setRawItems(built);
    };
    tick();
    const iv = setInterval(tick, 120_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, dismissed]);

  // Visible items = raw set minus dismissed keys. Computed every render so
  // dismissing an item updates immediately without a refetch.
  const items = rawItems.filter(i => !dismissed[i.key]);

  const handleItem = (it) => {
    if (it.to) navigate(it.to);
    setOpen(false);
  };

  const handleDismiss = (e, key) => {
    e.stopPropagation();
    const next = { ...dismissed, [key]: Date.now() };
    setDismissed(next);
    saveDismissals(next);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Alerts"
        className="nav-bell"
        aria-label="Alerts"
        aria-expanded={open}
        style={{
          position: 'relative', width: 34, height: 34, borderRadius: 'var(--r-md)',
          border: `1px solid ${open ? 'var(--border-strong)' : 'var(--border)'}`,
          background: open ? 'var(--bg-elevated)' : 'var(--bg-card)',
          color: 'var(--text-secondary)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Bell size={15} />
        {warnCount > 0 && (
          <span style={{
            position: 'absolute', top: 5, right: 6,
            minWidth: 14, height: 14, padding: '0 4px', borderRadius: 999,
            background: 'var(--red)', color: '#fff',
            fontSize: 9, fontWeight: 800, lineHeight: '14px', textAlign: 'center',
            boxShadow: '0 0 0 2px var(--bg-card)',
          }}>{warnCount}</span>
        )}
      </button>

      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 300,
          width: 'min(360px, calc(100vw - 24px))',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--shadow-lift)', overflow: 'hidden',
          animation: 'fadeIn var(--dur-fast) var(--ease-out) both',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)',
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>Notifications</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close notifications"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', padding: 2, display: 'flex',
              }}
            ><X size={14} /></button>
          </div>

          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            {loading && items.length === 0 && (
              <div style={{ padding: 18, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 22, fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'center' }}>
                Nothing to report. Trial / subscription warnings, auto-closes, expiring options, and recent trades will appear here.
              </div>
            )}
            {items.map((it) => {
              const s = KIND_STYLE[it.kind] || KIND_STYLE.info;
              const IconC = it.icon;
              return (
                <div
                  key={it.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '11px 14px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <button
                    onClick={() => handleItem(it)}
                    disabled={!it.to}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: 0, border: 'none', background: 'transparent',
                      cursor: it.to ? 'pointer' : 'default',
                      fontFamily: 'inherit', textAlign: 'left',
                    }}
                  >
                    <span style={{
                      width: 28, height: 28, borderRadius: 'var(--r-sm)', flexShrink: 0,
                      background: s.tint, border: `1px solid ${s.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: s.color,
                    }}>
                      <IconC size={13} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12.5, fontWeight: 700,
                        color: 'var(--text-primary)',
                      }}>{it.title}</span>
                      <span style={{
                        display: 'block', fontSize: 11.5, color: 'var(--text-secondary)',
                        lineHeight: 1.4, marginTop: 2,
                      }}>{it.body}</span>
                    </span>
                    {it.to && <ChevronRight size={14} color="var(--text-muted)" style={{ flexShrink: 0, marginTop: 4 }} />}
                  </button>
                  {!it.pinned && (
                    <button
                      onClick={(e) => handleDismiss(e, it.key)}
                      aria-label="Dismiss notification"
                      title="Dismiss"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'var(--text-faint)', padding: 2, display: 'flex',
                        flexShrink: 0, marginTop: 2,
                      }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
